import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  catchError,
  finalize,
  Observable,
  of,
  share,
  switchMap,
  throwError,
  tap,
} from 'rxjs';
import apiEndpoints from './api-endpoints.misc';
import { getAuthToken } from './headers.misc';
import { Router } from '@angular/router';
import { storageHelper } from './storage.misc';
import { SweetAlertService } from '../services/sweetalert.service';
import { AuthService } from '../services/auth.service';

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
  private excludedUrls: string[];
  private filesUrls: string[];
  totalRequests = 0;
  requestsCompleted = 0;

  constructor(
    private authService: AuthService,
    private sweetAlertService: SweetAlertService,
    private router: Router
  ) {
    this.excludedUrls = [
      apiEndpoints.signinUrl,
      apiEndpoints.signupUrl,
      apiEndpoints.registerUrl,
      apiEndpoints.forgotPasswordUrl,
      apiEndpoints.resetPasswordUrl,
      apiEndpoints.configsSiteUrl,
    ];

    this.filesUrls = [apiEndpoints.uploadProfileUrl];
  }

  notifyRequestSuccess(request: HttpRequest<any>) {
    if (this.excludedUrls.includes(request.url) || request.method === 'GET')
      return;
  }

  notifyRequestFailure(request: HttpRequest<any>) {
    if (this.excludedUrls.includes(request.url) || request.method === 'GET')
      return;
  }

  notifyAuthSuccess(authRequest: HttpRequest<any>, successData: any) {
    if (authRequest.url !== apiEndpoints.signinUrl) return;
    this.authService.onAuthSuccess(successData);
  }

  notifyAuthFailure(authRequest: HttpRequest<any>, errorData: any) {
    if (authRequest.url !== apiEndpoints.signinUrl) return;
    this.authService.onAuthFailure(errorData);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Ne pas modifier les requêtes externes (ex: Finnhub) pour éviter CORS/preflight inutile.
    if (!this.isInternalApiUrl(request.url)) {
      return next.handle(request);
    }

    this.totalRequests = this.isValidUrlForInterceptor(request.url)
      ? this.totalRequests + 1
      : this.totalRequests;

    const token = getAuthToken();
    let clonedRequest: HttpRequest<any>;

    // ---------- FORM DATA ----------
    if (request.body instanceof FormData) {
      let headers = request.headers;

      if (token) {
        headers = headers.set('Authorization', `Bearer ${token}`);
      }

      // 🔥 IMPORTANT : retirer Content-Type s’il existe
      headers = headers.delete('Content-Type');

      clonedRequest = request.clone({ headers });
    }


    // ---------- URL PROTÉGÉES ----------
    else if (this.isValidUrlForInterceptor(request.url)) {
      if (!this.authService.isAuthenticated && this.authService.accessToken) {
        this.sweetAlertService.toastWarning(
          'Session expirée !',
          3000,
          'Veuillez vous connecter à nouveau'
        );
        storageHelper.local.clear();
        this.router.navigateByUrl(`/`);
      }

      const hdr: any = {
        'Accept': 'application/json',
      };

      if (request.method !== 'GET') {
        hdr['Content-Type'] = 'application/json';
      }

      if (token) hdr['Authorization'] = `Bearer ${token}`;

      clonedRequest = request.clone({ setHeaders: hdr });
    }

    // ---------- URL PUBLIQUES ----------
    else {
      const hdr: any = { 'Accept': 'application/json' };
      if (request.method !== 'GET') {
        hdr['Content-Type'] = 'application/json';
      }

      clonedRequest = request.clone({ setHeaders: hdr });
    }

    return next.handle(clonedRequest).pipe(
      switchMap((event) => {
        if (event instanceof HttpResponse) {
          this.notifyRequestSuccess(clonedRequest);
          this.notifyAuthSuccess(clonedRequest, event.body);
        }
        return of(event);
      }),
      tap({
        error: (error: HttpErrorResponse) => {
          this.notifyRequestFailure(clonedRequest);
          this.notifyAuthFailure(clonedRequest, error.error);

          if (error.status === 401) {
            // this.sweetAlertService.toastWarning(
            //   'Accès non autorisé !',
            //   3000,
            //   'Veuillez vous connecter à nouveau'
            // );
            // storageHelper.local.clear();
            // this.router.navigateByUrl(`/`);
          }
        },
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
      finalize(() => {
        this.requestsCompleted++;
        if (this.requestsCompleted === this.totalRequests) {
          this.totalRequests = 0;
          this.requestsCompleted = 0;
        }
      }),
      share()
    );
  }

  private isValidUrlForInterceptor(url: string) {
    return !this.excludedUrls.includes(url);
  }

  private isInternalApiUrl(url: string): boolean {
    return url.startsWith(apiEndpoints.apiUrlBase);
  }
}
